import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowLeft } from "lucide-react";
import { Checkbox } from "./ui/checkbox";

const ease = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    key: "goal",
    kicker: "Obiettivo",
    question: "Qual è il tuo obiettivo principale?",
    options: [
      { value: "dimagrire", label: "Dimagrire" },
      { value: "tonificare", label: "Tonificare il corpo" },
      { value: "forza_massa", label: "Aumentare forza e massa muscolare" },
      { value: "costanza", label: "Tornare ad allenarmi con costanza" },
      { value: "salute_energia", label: "Migliorare salute ed energia" },
    ],
  },
  {
    key: "frequency",
    kicker: "Frequenza",
    question: "Quante volte riesci ad allenarti durante la settimana?",
    options: [
      { value: "1_volta", label: "1 volta" },
      { value: "2_volte", label: "2 volte" },
      { value: "3_volte", label: "3 volte" },
      { value: "piu_di_3", label: "Più di 3 volte" },
      { value: "non_mi_alleno", label: "Attualmente non mi alleno" },
    ],
  },
  {
    key: "timing",
    kicker: "Tempistiche",
    question: "Quando vorresti iniziare il tuo percorso?",
    options: [
      { value: "subito", label: "Subito" },
      { value: "entro_un_mese", label: "Entro un mese" },
      { value: "da_settembre", label: "Da settembre" },
      { value: "raccolgo_info", label: "Sto solo raccogliendo informazioni" },
    ],
  },
  {
    key: "contact_method",
    kicker: "Contatto",
    question: "Come preferisci essere ricontattato?",
    options: [
      { value: "telefono", label: "Telefonata" },
      { value: "whatsapp", label: "WhatsApp" },
      { value: "email", label: "Email" },
    ],
  },
  { key: "form", kicker: "Dettagli", question: "Perfetto! Abbiamo quasi finito." },
];

const TOTAL = STEPS.length;

export default function Quiz({ autoStart = false }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    privacy_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (autoStart) sectionRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [autoStart]);

  const progress = useMemo(() => {
    if (done) return 100;
    return Math.min(100, (step / TOTAL) * 100 + (step === TOTAL - 1 ? 10 : 0));
  }, [step, done]);

  const currentStep = STEPS[step];

  const handleSelect = useCallback(
    (key, value) => {
      setAnswers((prev) => ({ ...prev, [key]: value }));
      // auto-advance
      setTimeout(() => {
        setStep((s) => Math.min(s + 1, TOTAL - 1));
      }, 340);
    },
    []
  );

  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(0, s - 1));
  };

  const submitLead = async () => {
    setError(null);
    if (!form.full_name || !form.email || !form.phone) {
      setError("Compila tutti i campi obbligatori.");
      return;
    }
    if (!form.privacy_accepted) {
      setError("Devi accettare la privacy policy per continuare.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        privacy_accepted: form.privacy_accepted,
        answers,
      });
      // Meta Pixel — Lead event on successful submission
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        try {
          window.fbq("track", "Lead", {
            content_name: "BBfit Consulenza Quiz",
            content_category: answers?.goal,
          });
        } catch (_) { /* noop */ }
      }
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail.map((d) => d?.msg || "Campo non valido").join(" · ")
          : detail || "Qualcosa è andato storto. Riprova."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quiz"
      ref={sectionRef}
      data-testid="quiz-section"
      className="relative bg-obsidian min-h-[100svh] flex flex-col justify-center py-24 md:py-32 overflow-hidden border-t border-cream/10"
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-px bg-cream/10 z-20">
        <motion.div
          data-testid="quiz-progress"
          className="h-full bg-gold origin-left"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: progress / 100 }}
          transition={{ duration: 0.7, ease }}
          style={{ transformOrigin: "left" }}
        />
      </div>

      {/* Sticky top bar */}
      <div className="absolute top-6 md:top-10 left-0 right-0 z-10 max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between">
        <div className="text-[0.65rem] uppercase tracking-[0.4em] text-cream/50">
          <span className="text-gold">Consulenza</span> — Quiz
        </div>
        <div
          data-testid="quiz-step-indicator"
          className="text-[0.65rem] uppercase tracking-[0.4em] text-cream/50"
        >
          {done ? "Completato" : `Step ${step + 1} di ${TOTAL}`}
        </div>
      </div>

      <div className="relative z-10 max-w-[1000px] mx-auto w-full px-6 md:px-12 pt-16 md:pt-8">
        <AnimatePresence mode="wait">
          {done ? (
            <ThankYou key="thanks" />
          ) : currentStep.key === "form" ? (
            <FinalForm
              key="form"
              form={form}
              setForm={setForm}
              submitLead={submitLead}
              submitting={submitting}
              error={error}
              onBack={goBack}
            />
          ) : (
            <StepView
              key={currentStep.key}
              step={currentStep}
              stepIndex={step}
              onSelect={handleSelect}
              selected={answers[currentStep.key]}
              onBack={step > 0 ? goBack : null}
            />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

/* --- Step view --- */
function StepView({ step, stepIndex, onSelect, selected, onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.55, ease }}
      className="w-full"
      data-testid={`quiz-step-${stepIndex + 1}`}
    >
      <div className="flex items-center gap-3 mb-8">
        <span className="text-[0.68rem] uppercase tracking-[0.38em] text-gold">
          {String(stepIndex + 1).padStart(2, "0")} — {step.kicker}
        </span>
      </div>

      <h2 className="font-serif font-light text-cream text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] mb-12 md:mb-16 max-w-3xl">
        {step.question}
      </h2>

      <div className="flex flex-col gap-3">
        {step.options.map((opt, i) => (
          <motion.button
            key={opt.value}
            type="button"
            onClick={() => onSelect(step.key, opt.value)}
            data-testid={`quiz-option-${step.key}-${opt.value}`}
            className={`quiz-option ${
              selected === opt.value ? "border-gold text-cream" : ""
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.1 + i * 0.05 }}
            whileHover={{ x: 4 }}
          >
            <span className="flex items-center gap-6">
              <span className="qopt-index">
                {String.fromCharCode(65 + i)}
              </span>
              <span>{opt.label}</span>
            </span>
            <span className="qopt-arrow">
              <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
                <path d="M0 5H21M21 5L17 1M21 5L17 9" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </span>
          </motion.button>
        ))}
      </div>

      {onBack && (
        <div className="mt-12 flex items-center">
          <button
            type="button"
            onClick={onBack}
            data-testid="quiz-back"
            className="group flex items-center gap-3 text-cream/60 hover:text-gold text-[0.7rem] uppercase tracking-[0.28em] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.2} className="transition-transform group-hover:-translate-x-1" />
            Indietro
          </button>
        </div>
      )}
    </motion.div>
  );
}

/* --- Final form step --- */
function FinalForm({ form, setForm, submitLead, submitting, error, onBack }) {
  const change = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.55, ease }}
      className="w-full"
      data-testid="quiz-final-form"
    >
      <div className="flex items-center gap-3 mb-8">
        <span className="text-[0.68rem] uppercase tracking-[0.38em] text-gold">
          05 — Dettagli
        </span>
      </div>

      <h2 className="font-serif font-light text-cream text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] mb-6 max-w-3xl">
        Perfetto! <em className="italic text-gold/90">Abbiamo quasi finito.</em>
      </h2>
      <p className="text-cream/70 font-light text-base md:text-lg leading-relaxed max-w-2xl mb-12 md:mb-14">
        Lasciaci i tuoi dati e ti contatteremo per prenotare la tua consulenza
        gratuita.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitLead();
        }}
        className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10"
      >
        <div className="md:col-span-2">
          <label className="text-[0.62rem] uppercase tracking-[0.32em] text-cream/45">
            Nome e Cognome
          </label>
          <input
            type="text"
            required
            value={form.full_name}
            onChange={change("full_name")}
            placeholder="Il tuo nome"
            className="editorial-input"
            data-testid="lead-full-name"
          />
        </div>
        <div>
          <label className="text-[0.62rem] uppercase tracking-[0.32em] text-cream/45">
            Email
          </label>
          <input
            type="email"
            required
            value={form.email}
            onChange={change("email")}
            placeholder="tu@email.com"
            className="editorial-input"
            data-testid="lead-email"
          />
        </div>
        <div>
          <label className="text-[0.62rem] uppercase tracking-[0.32em] text-cream/45">
            Numero di telefono
          </label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={change("phone")}
            placeholder="+39 ..."
            className="editorial-input"
            data-testid="lead-phone"
          />
        </div>

        <div className="md:col-span-2 mt-2 flex items-start gap-3">
          <Checkbox
            id="privacy"
            checked={form.privacy_accepted}
            onCheckedChange={(v) =>
              setForm((p) => ({ ...p, privacy_accepted: Boolean(v) }))
            }
            data-testid="lead-privacy"
            className="mt-1 border-cream/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold data-[state=checked]:text-obsidian"
          />
          <label htmlFor="privacy" className="text-cream/60 font-light text-sm leading-relaxed cursor-pointer">
            Ho letto e accetto la{" "}
            <a href="#" className="text-gold underline underline-offset-4 decoration-gold/40 hover:decoration-gold">
              privacy policy
            </a>{" "}
            e autorizzo il trattamento dei miei dati per essere ricontattato.
          </label>
        </div>

        {error && (
          <div
            data-testid="quiz-error"
            className="md:col-span-2 border border-destructive/50 bg-destructive/10 text-cream/90 px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        <div className="md:col-span-2 mt-6 flex flex-col md:flex-row md:items-center gap-6 md:justify-between">
          <button
            type="button"
            onClick={onBack}
            data-testid="quiz-back-form"
            className="group flex items-center gap-3 text-cream/60 hover:text-gold text-[0.7rem] uppercase tracking-[0.28em] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.2} className="transition-transform group-hover:-translate-x-1" />
            Indietro
          </button>

          <button
            type="submit"
            disabled={submitting}
            data-testid="lead-submit"
            className="group relative overflow-hidden bg-gold hover:bg-gold-hover text-obsidian px-8 md:px-12 py-5 md:py-6 font-sans uppercase tracking-[0.22em] text-[0.72rem] md:text-[0.78rem] font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-500"
          >
            <span className="relative z-10 flex items-center justify-center gap-4">
              {submitting ? "Invio in corso…" : "Prenota la mia consulenza gratuita"}
              {!submitting && (
                <svg width="20" height="10" viewBox="0 0 22 10" fill="none" className="transition-transform duration-500 group-hover:translate-x-1">
                  <path d="M0 5H21M21 5L17 1M21 5L17 9" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              )}
            </span>
          </button>
        </div>
      </form>
    </motion.div>
  );
}

/* --- Thank you --- */
function ThankYou() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9, ease }}
      className="w-full text-center max-w-3xl mx-auto"
      data-testid="quiz-thank-you"
    >
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 0.9, ease, delay: 0.2 }}
        className="w-16 h-16 rounded-full border border-gold flex items-center justify-center mx-auto mb-10"
      >
        <Check size={22} className="text-gold" strokeWidth={1.4} />
      </motion.div>

      <div className="text-[0.68rem] uppercase tracking-[0.4em] text-gold mb-8">
        Richiesta ricevuta
      </div>

      <h2 className="font-serif font-light text-cream text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] mb-8">
        <em className="italic">Grazie.</em>
        <br />
        A presto in studio.
      </h2>

      <p className="text-cream/70 font-light text-base md:text-lg leading-relaxed max-w-xl mx-auto">
        Abbiamo ricevuto la tua richiesta. Ti contatteremo entro 24 ore per
        fissare la tua consulenza gratuita.
      </p>

      <div className="mt-16 divider-thin" />
      <p className="mt-6 text-[0.65rem] uppercase tracking-[0.35em] text-cream/40">
        BBfit Studio — Conegliano
      </p>
    </motion.div>
  );
}
